/* global React */
import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import ApplicationContext from '@platform/react/context/application'
import PlatformEvent from 'lib/util/event'

const _getCountData = ({ count, data = {} } = {}) => (count ? { count, data } : { count: 0 })

/**
 * High Order Component for displaying count or single entry data
 *
 * @example client/platform/web/src/ui/Element/Subbed/Item/List/Cell/Count.jsx
 *
 * @param {React.Element} Component
 * @returns {React.Element}
 */
const CountHoc = Component => (props) => {
  const { socket: { ref: socket } } = useContext(ApplicationContext)
  const {
    count: {
      value, options = {}, ref, handler,
    } = {},
  } = props

  const socketKeys = useRef(
    Object.keys(options).map(o => ({
      ref,
      docType: o,
      key: `${ref}_${o}`,
      refType: Object.keys(options[o]).pop(),
    })),
  ).current

  // useEffect(() => {
  //   socketKeys.current = countTypes.map(t => `${t.ref}_${t.docType}`)
  // }, [countTypes])

  // const docType = Object.keys(options).pop()
  // const refType = Object.keys(options[docType]).pop()
  // const socketKey = useRef(`${ref}_${docType}`).current
  // const initialValue = _getCountData(value?.[docType]?.[refType])
  const [countValue, setValue] = useState(
    socketKeys.reduce((acc, s) => (
      { ...acc, [s.docType]: { [s.refType]: _getCountData(value?.[s.docType]?.[s.refType]) } }
    ), {}),
  )

  const listener = useCallback(s => async () => {
    const event = new PlatformEvent('count', { detail: { key: s.ref, type: s.docType } })
    const result = await handler(event)

    setValue(prevValue => ({
      ...prevValue,
      [s.docType]: { [s.refType]: _getCountData(result[[s.refType]] || result.total) },
    }))
  }, [])

  useLayoutEffect(() => {
    const listenerFns = socketKeys.map(s => listener(s))

    socketKeys.forEach((s, i) => {
      socket.sub(s.key, () => {
        socket.on(s.key, listenerFns[i])
      })
    })

    return () => {
      socketKeys.forEach((s, i) => {
        socket.unsub(s.key, () => {
          socket.off(s.key, listenerFns[i])
        })
      })
    }
  }, [socketKeys])

  return (
    <Component
      {...props}
      count={countValue}
    />
  )
}

export default CountHoc
